import React, { useState, useEffect, useRef } from 'react';
import { Priority } from '../../types';
import { generateTaskDetailsFromText } from './aiService';
import { useFocusTrap } from '../../hooks/useFocusTrap';

/**
 * Props for the AddModal component.
 */
interface AddModalProps {
  onClose: () => void;
  onSave: (title: string, description: string, priority: Priority) => Promise<void>;
}

const priorityOptions = [Priority.Critical, Priority.High, Priority.Medium, Priority.Low];

// Style mappings for priority selection chips based on their state (selected/unselected).
const priorityChipStyles: { [key in Priority]: { selected: string; unselected:string } } = {
    [Priority.Critical]: { selected: 'bg-red-600 text-white border-red-600',    unselected: 'text-red-700 bg-white border-red-300 hover:bg-red-50 hover:border-red-500' },
    [Priority.High]:   { selected: 'bg-orange-500 text-white border-orange-500',    unselected: 'text-orange-700 bg-white border-orange-300 hover:bg-orange-50 hover:border-orange-500' },
    [Priority.Medium]: { selected: 'bg-yellow-500 text-white border-yellow-500', unselected: 'text-yellow-800 bg-white border-yellow-400 hover:bg-yellow-50 hover:border-yellow-600' },
    [Priority.Low]:    { selected: 'bg-green-600 text-white border-green-600',  unselected: 'text-green-800 bg-white border-green-400 hover:bg-green-50 hover:border-green-600' },
};

// Defines the two steps of the modal: user input and AI suggestion review.
type Step = 'input' | 'review';

/**
 * A modal for adding a new maintenance task. It features a two-step process:
 * 1. User enters a natural language description of the task.
 * 2. An AI service generates a structured title, description, and priority for the user to review and edit.
 * @param {AddModalProps} props - The props for the component.
 * @returns {React.ReactElement} - The rendered 'Add Task' modal.
 */
const AddModal: React.FC<AddModalProps> = ({ onClose, onSave }) => {
  const [step, setStep] = useState<Step>('input');
  // State for each form field
  const [naturalLanguageInput, setNaturalLanguageInput] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<Priority>(Priority.Medium);
  // State for async operations
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  // State for error display and modal animation
  const [error, setError] = useState<string | null>(null);
  const [isShowing, setIsShowing] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Apply the focus trap when the modal is visible.
  useFocusTrap(modalRef, isShowing);

  // Handle modal animation and Escape key listener on mount.
  useEffect(() => {
    setIsShowing(true);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  /**
   * Closes the modal with a fade-out animation. Prevents closing if an async operation is in progress.
   */
  const handleClose = () => {
    if (isSaving || isGenerating) return;
    setIsShowing(false);
    setTimeout(onClose, 300);
  };

  /**
   * Calls the AI service to generate structured task details from the user's input.
   * Transitions to the 'review' step on success.
   */
  const handleGenerate = async () => {
    if (isGenerating || !naturalLanguageInput.trim()) return;
    setIsGenerating(true);
    setError(null);
    try {
        const details = await generateTaskDetailsFromText(naturalLanguageInput);
        setTitle(details.title);
        setDescription(details.description);
        setPriority(details.priority);
        setStep('review');
    } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred.');
    } finally {
        setIsGenerating(false);
    }
  };

  /**
   * Returns the user to the initial input step, clearing all generated data.
   */
  const handleStartOver = () => {
    setStep('input');
    setError(null);
    setTitle('');
    setDescription('');
    setPriority(Priority.Medium);
  };

  /**
   * Handles the final submission of the new task form.
   * @param {React.FormEvent} e - The form event.
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSaving || !title.trim() || !description.trim()) return;
    
    setIsSaving(true);
    setError(null);
    try {
        // Call the parent's onSave function
        await onSave(title.trim(), description.trim(), priority);
        handleClose(); // Close modal on successful save
    } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(`Failed to create the item. Please try again. Error: ${errorMessage}`);
    } finally {
        setIsSaving(false);
    }
  };
  
  /**
   * Renders the content for the initial 'input' step.
   */
  const renderInputStep = () => (
    <>
      <div className="p-6 space-y-4">
          <div>
            <label htmlFor="naturalLanguageInput" className="block text-sm font-medium text-gray-700 mb-1">Describe the maintenance task</label>
            <textarea
              id="naturalLanguageInput"
              value={naturalLanguageInput}
              onChange={(e) => setNaturalLanguageInput(e.target.value)}
              rows={6}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="e.g., The air conditioner in room 301 is making a loud rattling noise and isn't cooling properly. It seems urgent."
            />
            <p className="mt-2 text-xs text-gray-500">Our AI assistant will help generate a title, description, and priority from your text.</p>
          </div>
          {error && <p className="text-sm text-red-600 bg-red-50 p-3 rounded-md">{error}</p>}
      </div>
      <div className="bg-gray-50 px-6 py-4 flex justify-end items-center space-x-3 rounded-b-xl">
        <button
          type="button"
          onClick={handleClose}
          className="px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleGenerate}
          disabled={isGenerating || !naturalLanguageInput.trim()}
          className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-300 disabled:cursor-not-allowed"
        >
          {isGenerating ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Generating...
            </>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" className="-ml-1 mr-2 h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              Generate with AI
            </>
          )}
        </button>
      </div>
    </>
  );

  /**
   * Renders the content for the 'review' step, where the user can edit and confirm the AI's suggestions.
   */
  const renderReviewStep = () => (
    <form onSubmit={handleSubmit}>
      <div className="p-6 space-y-6">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              required
            />
          </div>
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              required
            />
          </div>
          <div>
            <span className="block text-sm font-medium text-gray-700 mb-2">Priority</span>
            <fieldset className="flex flex-wrap gap-3">
              <legend className="sr-only">Priority options</legend>
              {priorityOptions.map((p) => (
                <label 
                  key={p}
                  htmlFor={`priority-${p}`}
                  className={`px-4 py-2 rounded-full cursor-pointer border-2 font-medium text-sm transition-all duration-200 ease-in-out transform hover:scale-105 ${
                    priority === p ? priorityChipStyles[p].selected : priorityChipStyles[p].unselected
                  }`}
                >
                  <input
                    id={`priority-${p}`}
                    name="priority"
                    type="radio"
                    value={p}
                    checked={priority === p}
                    onChange={() => setPriority(p)}
                    className="sr-only"
                    aria-labelledby={`priority-${p}-label`}
                  />
                  <span id={`priority-${p}-label`}>
                    {p}
                  </span>
                </label>
              ))}
            </fieldset>
          </div>
          {error && <p className="text-sm text-red-600 bg-red-50 p-3 rounded-md">{error}</p>}
      </div>
      <div className="bg-gray-50 px-6 py-4 flex justify-between items-center rounded-b-xl">
        <button
          type="button"
          onClick={handleStartOver}
          className="px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Start Over
        </button>
        <button
          type="submit"
          disabled={isSaving || !title.trim() || !description.trim()}
          className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-300 disabled:cursor-not-allowed"
        >
          {isSaving ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Creating...
            </>
          ) : (
            <>
            <svg xmlns="http://www.w3.org/2000/svg" className="-ml-1 mr-2 h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Create Task
            </>
          )}
        </button>
      </div>
    </form>
  );

  return (
    <div
      className={`fixed inset-0 bg-black z-50 flex justify-center items-center transition-opacity duration-300 ${isShowing ? 'bg-opacity-60' : 'bg-opacity-0'}`}
      aria-labelledby="add-modal-title"
      role="dialog"
      aria-modal="true"
      onClick={handleClose}
    >
      <div
        ref={modalRef}
        className={`bg-white rounded-xl shadow-2xl w-full max-w-lg m-4 transform transition-all duration-300 ease-out ${isShowing ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}
        role="document"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 id="add-modal-title" className="text-xl font-bold text-gray-900">Add New Task {step === 'review' && <span className="text-base font-normal text-gray-500">(Review AI Suggestions)</span>}</h2>
            <button 
              type="button" 
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 rounded-full p-1 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Render the content based on the current step */}
        {step === 'input' ? renderInputStep() : renderReviewStep()}
        
      </div>
    </div>
  );
};

export default AddModal;
